import { create } from "zustand";

/**
 * Checkbox draft for the Welcome / terms screen. Lives in memory (not
 * persisted) so selections survive client-side navigation to a legal
 * document and back. Scoped per gallery so one flow does not inherit
 * another's checks. Mirrors `useTermsDraftStore` in fund-request / request.
 */
interface TermsDraftState {
  scope: string | null;
  checkedById: Record<string, boolean>;
  isESignChecked: boolean;
  isTermsChecked: boolean;
  setEntryChecked: (scope: string, entryId: string, checked: boolean) => void;
  setESignChecked: (scope: string, checked: boolean) => void;
  setTermsChecked: (scope: string, checked: boolean) => void;
  reset: () => void;
}

const empty = {
  scope: null as string | null,
  checkedById: {} as Record<string, boolean>,
  isESignChecked: false,
  isTermsChecked: false,
};

const EMPTY_CHECKS: Record<string, boolean> = {};

function withScope(
  state: TermsDraftState,
  scope: string,
  patch: Partial<Pick<TermsDraftState, "checkedById" | "isESignChecked" | "isTermsChecked">>,
): Pick<TermsDraftState, "scope" | "checkedById" | "isESignChecked" | "isTermsChecked"> {
  if (state.scope === scope) {
    return {
      scope,
      checkedById: state.checkedById,
      isESignChecked: state.isESignChecked,
      isTermsChecked: state.isTermsChecked,
      ...patch,
    };
  }
  return { ...empty, ...patch, scope };
}

export const useTermsDraftStore = create<TermsDraftState>((set) => ({
  ...empty,
  setEntryChecked: (scope, entryId, checked) =>
    set((state) => {
      const base = state.scope === scope ? state.checkedById : {};
      return withScope(state, scope, {
        checkedById: { ...base, [entryId]: checked },
      });
    }),
  setESignChecked: (scope, checked) =>
    set((state) => withScope(state, scope, { isESignChecked: checked })),
  setTermsChecked: (scope, checked) =>
    set((state) => withScope(state, scope, { isTermsChecked: checked })),
  reset: () => set(() => ({ ...empty })),
}));

/** Gallery binding: inactive scope reads as unchecked, writes adopt `scope`. */
export function useTermsDraft(scope: string) {
  const draftScope = useTermsDraftStore((s) => s.scope);
  const storedCheckedById = useTermsDraftStore((s) => s.checkedById);
  const storedESign = useTermsDraftStore((s) => s.isESignChecked);
  const storedTerms = useTermsDraftStore((s) => s.isTermsChecked);
  const setEntryChecked = useTermsDraftStore((s) => s.setEntryChecked);
  const setESignChecked = useTermsDraftStore((s) => s.setESignChecked);
  const setTermsChecked = useTermsDraftStore((s) => s.setTermsChecked);
  const reset = useTermsDraftStore((s) => s.reset);

  const active = draftScope === scope;

  return {
    checkedById: active ? storedCheckedById : EMPTY_CHECKS,
    isESignChecked: active && storedESign,
    isTermsChecked: active && storedTerms,
    setEntryChecked: (entryId: string, checked: boolean) =>
      setEntryChecked(scope, entryId, checked),
    setESignChecked: (checked: boolean) => setESignChecked(scope, checked),
    setTermsChecked: (checked: boolean) => setTermsChecked(scope, checked),
    reset,
  };
}
